import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Button, Card, Input, InputNumber } from 'antd';
import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { Check, Edit2, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';

const MASTER_CALC_STORAGE_KEY = 'nexrise.masterCalc.items';

const createEmptyItem = (id = Date.now()) => ({
  id,
  productId: '',
  sku: '',
  name: '',
  landingRate: '',
  qty: '',
  weight: 0,
  total: 0
});

const loadSavedItems = () => {
  try {
    const savedItems = localStorage.getItem(MASTER_CALC_STORAGE_KEY);
    if (!savedItems) return [createEmptyItem(1)];

    const parsedItems = JSON.parse(savedItems);
    if (!Array.isArray(parsedItems) || parsedItems.length === 0) {
      return [createEmptyItem(1)];
    }

    return parsedItems.map((item, index) => ({
      ...createEmptyItem(Date.now() + index),
      ...item,
      id: item.id || Date.now() + index,
      total: (Number(item.landingRate) || 0) * (Number(item.qty) || 0)
    }));
  } catch (error) {
    console.error("Error loading MasterCalc draft:", error);
    return [createEmptyItem(1)];
  }
};

const AdminMasterCalc = ({ db }) => {
  const [items, setItems] = useState(loadSavedItems);
  const [allProducts, setAllProducts] = useState([]);
  const [editingRateId, setEditingRateId] = useState(null);
  const [rateDraft, setRateDraft] = useState('');
  const [duplicateItemId, setDuplicateItemId] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const productSearchRefs = useRef({});
  const qtyRefs = useRef({});
  const rateInputRef = useRef(null);
  const pendingFocusItemId = useRef(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const prodSnap = await getDocs(collection(db, "products"));
        setAllProducts(prodSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error loading products for MasterCalc:", error);
      }
    };

    loadProducts();
  }, [db]);

  useEffect(() => {
    try {
      localStorage.setItem(MASTER_CALC_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error("Error saving MasterCalc draft:", error);
    }
  }, [items]);

  const productSearchOptions = useMemo(() => allProducts.map((product) => ({
    value: product.sku || '',
    product,
    searchText: `${product.sku || ''} ${product.name || ''}`.toLowerCase(),
    label: (
      <div style={{ lineHeight: 1.3 }}>
        <div style={{ fontWeight: '700', fontSize: '12px' }}>{product.sku || 'No SKU'}</div>
        <div style={{ fontSize: '11px', color: '#64748b' }}>{product.name || 'Unnamed product'}</div>
      </div>
    )
  })), [allProducts]);

  const updateItem = (id, field, value) => {
    setItems((currentItems) => currentItems.map((item) => {
      if (item.id !== id) return item;

      const updatedItem = { ...item, [field]: value };
      const landingRate = Number(updatedItem.landingRate) || 0;
      const qty = Number(updatedItem.qty) || 0;

      return {
        ...updatedItem,
        total: landingRate * qty
      };
    }));

  };

  const updateSkuSearch = (id, value) => {
    setItems((currentItems) => currentItems.map((item) => {
      if (item.id !== id) return item;

      return {
        ...item,
        productId: '',
        sku: value,
        name: '',
        landingRate: '',
        qty: '',
        weight: 0,
        total: 0
      };
    }));
  };

  const focusQtyForItem = (id) => {
    setTimeout(() => qtyRefs.current[id]?.focus(), 0);
  };

  const selectProduct = (id, product) => {
    const existingItem = items.find((item) =>
      item.id !== id &&
      (
        (product.id && item.productId === product.id) ||
        (product.sku && item.sku === product.sku)
      )
    );

    if (existingItem) {
      setDuplicateItemId(existingItem.id);
      toast.error(`${product.sku || 'This product'} is already added.`);
      return;
    }

    setItems((currentItems) => currentItems.map((item) => {
      if (item.id !== id) return item;

      const landingRate = Number(product.landingRate) || 0;
      const qty = Number(item.qty) || 0;

      return {
        ...item,
        productId: product.id || '',
        sku: product.sku || '',
        name: product.name || '',
        landingRate,
        weight: Number(product.weight) || 0,
        total: landingRate * qty
      };
    }));
    focusQtyForItem(id);
  };

  const addItem = () => {
    const nextItemId = Date.now();
    pendingFocusItemId.current = nextItemId;
    setItems((currentItems) => [
      ...currentItems,
      createEmptyItem(nextItemId)
    ]);
  };

  const removeItem = (id) => {
    setItems((currentItems) => currentItems.filter((item) => item.id !== id));
  };

  const resetCalculator = () => {
    const emptyItemId = Date.now();
    pendingFocusItemId.current = emptyItemId;
    setItems([createEmptyItem(emptyItemId)]);
    setEditingRateId(null);
    setRateDraft('');
    setDuplicateItemId(null);
    productSearchRefs.current = {};
    qtyRefs.current = {};
    localStorage.removeItem(MASTER_CALC_STORAGE_KEY);
  };

  const startEditingRate = (item) => {
    setEditingRateId(item.id);
    setRateDraft(item.landingRate);
    setTimeout(() => rateInputRef.current?.focus(), 0);
  };

  const confirmRateUpdate = async (id) => {
    const item = items.find((currentItem) => currentItem.id === id);
    if (!item?.productId) {
      toast.error('Select a product from SKU search before updating landing rate.');
      return;
    }

    const nextLandingRate = Number(rateDraft) || 0;

    try {
      await updateDoc(doc(db, "products", item.productId), {
        landingRate: nextLandingRate
      });

      updateItem(id, 'landingRate', nextLandingRate);
      setAllProducts((currentProducts) => currentProducts.map((product) => (
        product.id === item.productId
          ? { ...product, landingRate: nextLandingRate }
          : product
      )));
      setEditingRateId(null);
      setRateDraft('');
      toast.success(`Landing rate updated for ${item.sku || item.name || 'product'}.`);
    } catch (error) {
      console.error("Error updating landing rate:", error);
      toast.error("Unable to update landing rate: " + error.message);
    }
  };

  const cancelRateUpdate = () => {
    setEditingRateId(null);
    setRateDraft('');
  };

  const subTotal = items.reduce((sum, item) => sum + item.total, 0);
  const gst = subTotal * 0.18;
  const grandTotal = subTotal + gst;
  const totalWeight = items.reduce((sum, item) => {
    const qty = Number(item.qty) || 0;
    const weight = Number(item.weight) || 0;
    return sum + (weight * qty);
  }, 0);

  useEffect(() => {
    if (!pendingFocusItemId.current) return;

    const focusId = pendingFocusItemId.current;
    pendingFocusItemId.current = null;
    setTimeout(() => productSearchRefs.current[focusId]?.focus(), 0);
  }, [items]);

  useEffect(() => {
    if (!duplicateItemId) return undefined;

    const timer = setTimeout(() => {
      setDuplicateItemId(null);
    }, 1800);

    return () => clearTimeout(timer);
  }, [duplicateItemId]);

  const isMobile = windowWidth <= 768;
  const containerStyle = {
    padding: isMobile ? '12px' : '20px'
  };
  const responsivePageGridStyle = {
    ...pageGridStyle,
    ...(isMobile ? mobilePageGridStyle : {})
  };
  const responsivePanelStyle = {
    ...panelStyle,
    ...(isMobile ? mobilePanelStyle : {})
  };
  const responsiveSummaryPanelStyle = {
    ...summaryPanelStyle,
    ...(isMobile ? mobileSummaryPanelStyle : {})
  };
  const responsiveItemGridStyle = {
    ...itemGridStyle,
    ...(isMobile ? mobileItemGridStyle : {})
  };
  const responsiveHeaderGridStyle = {
    ...headerGridStyle,
    ...(isMobile ? mobileHeaderGridStyle : {})
  };
  const responsiveHeadingStyle = {
    fontSize: isMobile ? '20px' : '24px',
    fontWeight: 'bold',
    margin: isMobile ? '0 0 14px 0' : '0 0 20px 0'
  };

  return (
    <div style={containerStyle}>
      <h1 style={responsiveHeadingStyle}>MasterCalc</h1>

      <div style={responsivePageGridStyle}>
        <div style={responsivePanelStyle}>
          <div style={responsiveHeaderGridStyle}>
            <div style={labelStyle}>SKU</div>
            <div style={labelStyle}>Name</div>
            <div style={labelStyle}>Landing rate</div>
            <div style={labelStyle}>Qty</div>
            <div style={labelStyle}>Total</div>
            <div style={labelStyle}></div>
          </div>
          <div style={responsiveItemGridStyle}>
            {items.map((item) => (
              <React.Fragment key={item.id}>
                <div>
                  <AutoComplete
                    ref={(element) => {
                      if (element) {
                        productSearchRefs.current[item.id] = element;
                      } else {
                        delete productSearchRefs.current[item.id];
                      }
                    }}
                    options={productSearchOptions}
                    value={item.sku}
                    onChange={(value) => updateSkuSearch(item.id, value)}
                    onSelect={(_, option) => selectProduct(item.id, option.product)}
                    placeholder="Search SKU..."
                    filterOption={(inputValue, option) => option?.searchText?.includes(String(inputValue || '').toLowerCase())}
                    notFoundContent="No matching product"
                    placement="bottomLeft"
                    popupMatchSelectWidth
                    size="large"
                    status={duplicateItemId === item.id ? 'warning' : undefined}
                    style={{ width: '100%' }}
                  />
                </div>
                <Input
                  value={item.name || ''}
                  readOnly
                  placeholder="-"
                  size="large"
                  status={duplicateItemId === item.id ? 'warning' : undefined}
                  style={readonlyCellStyle}
                />
                {editingRateId === item.id ? (
                  <div style={rateEditCellStyle}>
                    <InputNumber
                      ref={rateInputRef}
                      value={rateDraft}
                      disabled={!item.productId}
                      onChange={(value) => setRateDraft(value ?? '')}
                      controls={false}
                      size="large"
                      style={{
                        width: '100%',
                        ...rateInputStyle,
                        ...(!item.productId ? disabledInputStyle : {}),
                        ...(duplicateItemId === item.id ? duplicateFieldStyle : {})
                      }}
                    />
                    <Button
                      onClick={() => confirmRateUpdate(item.id)}
                      style={confirmButtonStyle}
                      title="Update price"
                      tabIndex={-1}
                      icon={<Check size={15} />}
                    >
                    </Button>
                    <Button
                      onClick={cancelRateUpdate}
                      style={cancelButtonStyle}
                      title="Cancel"
                      tabIndex={-1}
                      icon={<X size={15} />}
                    >
                    </Button>
                  </div>
                ) : (
                  <div style={{
                    ...rateViewCellStyle,
                    ...(duplicateItemId === item.id ? duplicateFieldStyle : {})
                  }}>
                    <span>₹{Number(item.landingRate || 0).toLocaleString('en-IN')}</span>
                    <Button
                      onClick={() => startEditingRate(item)}
                      disabled={!item.productId}
                      style={{
                        ...editButtonStyle,
                        ...(!item.productId ? disabledIconButtonStyle : {})
                      }}
                      title="Edit price"
                      tabIndex={-1}
                      icon={<Edit2 size={15} />}
                    >
                    </Button>
                  </div>
                )}
                <InputNumber
                  ref={(element) => {
                    if (element) {
                      qtyRefs.current[item.id] = element;
                    } else {
                      delete qtyRefs.current[item.id];
                    }
                  }}
                  value={item.qty}
                  disabled={!item.productId}
                  onChange={(value) => updateItem(item.id, 'qty', value ?? '')}
                  min={0}
                  step={1}
                  precision={0}
                  parser={(value) => String(value || '').replace(/[^\d]/g, '')}
                  size="large"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addItem();
                    }
                  }}
                  controls={false}
                  style={{
                    width: '100%',
                    ...inputStyle,
                    ...(!item.productId ? disabledInputStyle : {}),
                    ...(duplicateItemId === item.id ? duplicateFieldStyle : {})
                  }}
                />
                <Input
                  value={`Rs. ${item.total.toLocaleString('en-IN')}`}
                  readOnly
                  size="large"
                  status={duplicateItemId === item.id ? 'warning' : undefined}
                  style={totalStyle}
                />
                <Button
                  onClick={() => removeItem(item.id)}
                  style={{
                    ...deleteButtonStyle,
                    ...(duplicateItemId === item.id ? duplicateButtonStyle : {})
                  }}
                  title="Remove item"
                  tabIndex={-1}
                  icon={<Trash2 size={16} />}
                >
                </Button>
              </React.Fragment>
            ))}

            <div style={itemActionsRowStyle}>
              <Button type="primary" onClick={addItem} style={addButtonStyle}>Add Item</Button>
              <Button danger onClick={resetCalculator} style={resetButtonStyle}>Reset</Button>
            </div>
          </div>
        </div>

        <Card
          style={{ ...responsiveSummaryPanelStyle, padding: 0 }}
          styles={{ body: { padding: isMobile ? '15px' : '20px' } }}
        >
          <h2 style={summaryTitleStyle}>Summary</h2>
          <div style={summaryRowStyle}>
            <span>Sub Total(excl. tax):</span>
            <strong>₹{subTotal.toLocaleString('en-IN')}</strong>
          </div>
          <div style={summaryRowStyle}>
            <span>GST (18%):</span>
            <strong>₹{gst.toLocaleString('en-IN')}</strong>
          </div>
          <div style={summaryRowStyle}>
            <span>Total Weight:</span>
            <strong>{totalWeight.toLocaleString('en-IN')} kg</strong>
          </div>
          <div style={summaryDividerStyle} />
          <div style={summaryTotalRowStyle}>
            <span>Sub Total:</span>
            <strong>₹{grandTotal.toLocaleString('en-IN')}</strong>
          </div>
        </Card>
      </div>
    </div>
  );
};

const pageGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 4fr) minmax(220px, 1fr)',
  gap: '20px',
  alignItems: 'start'
};

const mobilePageGridStyle = {
  gridTemplateColumns: '1fr',
  gap: '12px'
};

const panelStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  padding: '20px',
  overflowX: 'auto'
};

const mobilePanelStyle = {
  padding: '12px',
  borderRadius: '10px',
  WebkitOverflowScrolling: 'touch'
};

const summaryPanelStyle = {
  ...panelStyle,
  position: 'sticky',
  top: '20px',
  alignSelf: 'start',
  height: 'fit-content',
  overflowX: 'visible'
};

const mobileSummaryPanelStyle = {
  position: 'static',
  top: 'auto',
  overflowX: 'visible'
};

const summaryTitleStyle = {
  fontSize: '18px',
  fontWeight: '700',
  margin: '0 0 18px 0',
  color: '#1e293b'
};

const summaryRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '8px 0',
  fontSize: '14px',
  color: '#475569'
};

const summaryDividerStyle = {
  borderTop: '1px solid #e2e8f0',
  margin: '12px 0'
};

const summaryTotalRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '16px',
  fontSize: '16px',
  fontWeight: '700',
  color: '#1e293b'
};

const itemGridStyle = {
  display: 'grid',
  gridTemplateColumns: '120px minmax(280px, 1.8fr) 130px 130px 130px 40px',
  gap: '10px',
  minWidth: '730px',
  alignItems: 'center'
};

const mobileItemGridStyle = {
  gridTemplateColumns: '120px 240px 110px 110px 110px 40px',
  minWidth: '770px',
  gap: '8px'
};

const headerGridStyle = {
  ...itemGridStyle,
  position: 'sticky',
  top: 0,
  zIndex: 4,
  backgroundColor: 'white',
  paddingBottom: '8px',
  marginBottom: '8px'
};

const mobileHeaderGridStyle = {
  ...mobileItemGridStyle,
  paddingBottom: '8px',
  marginBottom: '8px'
};

const labelStyle = {
  padding: '6px 0',
  fontSize: '12px',
  fontWeight: '700',
  color: '#64748b',
  textTransform: 'uppercase'
};

const fieldHeight = '40px';

const inputStyle = {
  width: '100%',
  height: fieldHeight,
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  borderColor: '#e2e8f0',
  boxShadow: 'none',
  backgroundColor: 'white',
  outline: 'none',
  fontSize: '14px',
  boxSizing: 'border-box'
};

const duplicateFieldStyle = {
  backgroundColor: '#fffbeb',
  borderColor: '#f59e0b',
  boxShadow: '0 0 0 2px rgba(245, 158, 11, 0.18)'
};

const duplicateButtonStyle = {
  backgroundColor: '#fffbeb',
  borderColor: '#f59e0b'
};

const totalStyle = {
  height: fieldHeight,
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderColor: '#e2e8f0',
  boxShadow: 'none',
  fontSize: '14px',
  boxSizing: 'border-box'
};

const disabledInputStyle = {
  backgroundColor: '#f1f5f9',
  color: '#94a3b8',
  cursor: 'not-allowed'
};

const readonlyCellStyle = {
  height: fieldHeight,
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderColor: '#e2e8f0',
  boxShadow: 'none',
  fontSize: '14px',
  color: '#334155',
  boxSizing: 'border-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const rateViewCellStyle = {
  height: fieldHeight,
  padding: '6px 6px 6px 10px',
  borderRadius: '8px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderColor: '#e2e8f0',
  boxShadow: 'none',
  fontSize: '14px',
  color: '#334155',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px'
};

const rateEditCellStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 32px 32px',
  gap: '6px',
  alignItems: 'center'
};

const rateInputStyle = {
  ...inputStyle
};

const iconButtonBaseStyle = {
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const editButtonStyle = {
  ...iconButtonBaseStyle,
  border: '1px solid #dbeafe',
  backgroundColor: 'white',
  color: '#2563eb'
};

const disabledIconButtonStyle = {
  borderColor: '#e2e8f0',
  backgroundColor: '#f1f5f9',
  color: '#94a3b8',
  cursor: 'not-allowed'
};

const confirmButtonStyle = {
  ...iconButtonBaseStyle,
  border: '1px solid #bbf7d0',
  backgroundColor: '#f0fdf4',
  color: '#16a34a'
};

const cancelButtonStyle = {
  ...iconButtonBaseStyle,
  border: '1px solid #fee2e2',
  backgroundColor: '#fff',
  color: '#ef4444'
};

const itemActionsRowStyle = {
  gridColumn: '1 / -1',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: '4px'
};

const addButtonStyle = {
  padding: '10px 16px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: '#2563eb',
  color: 'white',
  fontWeight: '600',
  cursor: 'pointer'
};

const resetButtonStyle = {
  padding: '10px 16px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: '#dc2626',
  color: 'white',
  fontWeight: '600',
  cursor: 'pointer'
};

const deleteButtonStyle = {
  width: '40px',
  height: '39px',
  borderRadius: '8px',
  border: '1px solid #fee2e2',
  borderColor: '#fee2e2',
  boxShadow: 'none',
  backgroundColor: '#fff',
  color: '#ef4444',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

export default AdminMasterCalc;

